import { acceptHMRUpdate, defineStore } from "pinia";
import { nextTick, Ref, ref } from "vue";
import { RouteLocationNormalized, RouteLocationNormalizedLoaded, Router, RouteRecordName, useRouter } from "vue-router";
import { PATHS } from "@/router/paths";
import { usePrematchStore } from "@/prematch/stores/prematch-sports";
import usePrematchRouteIds from "@/prematch/composables/use-prematch-route-ids";
import { PrematchStaticCategories } from "@/prematch/model/enums/prematch-static-categories";
import useEventDetails from "@/shared/composables/use-event-details";
import { useLiveEventDetailStore } from "@/live/stores/live-event-detail";
import { useBetsStore } from "@/shared/stores/use-bets-store";
import useUtils from "@/shared/composables/use-utils";
import { logException } from "@/shared/utils/logger/use-app-logger";
import { BetData } from "@/shared/model/bet";
import { Sport } from "@/shared/model/enums/sport";
import useEventScoreAndStatus from "@/shared/composables/use-event-score-and-status";
import { DRILLDOWN_SPORT_LEVEL_NUMBER } from "../utils/constants";
import { getSharedTicket } from "@/api/v1-api";
import { useDrilldownNodesStore } from "@/shared/stores/use-drilldown-nodes-store";
import { useUiStateStore } from "@/shared/composables/use-ui-state";
import { waitUntilTrue } from "@/shared/utils/wait";
import { omit as _omit } from "lodash";

interface HeaderMeta {
    title: (r?: RouteLocationNormalized) => string | Promise<string>;
    subTitle?: (r?: RouteLocationNormalized) => string | Promise<string>;
    transparent?: boolean;
    showMiddleText?: boolean;
    backPath?: (r?: RouteLocationNormalized) => string;
    backgroundImage?: (r?: RouteLocationNormalized) => string | Promise<string>;
    displaySazkaBetLogo?: boolean;
    favoriteMatchId?: (r?: RouteLocationNormalized) => string | Promise<string>;
    closeIcon?: boolean;
    backPathPrevious?: boolean;
    savePreviousBackPath?: boolean;
}

interface RouteHeader extends HeaderMeta {
    name: RouteRecordName;
}

const activeTicket = async (ticketId) => {
    const betsStore = useBetsStore();
    await betsStore.waitUntilInitialized();
    const ticket = betsStore.allBets.find((bet) => bet.id === ticketId);
    return ticket;
};

const activeSharedTicket = async (ticketId) => {
    return await getSharedTicket(ticketId);
};

const liveEventTitle = async () => {
    const liveEventStore = useLiveEventDetailStore();
    await liveEventStore.waitUntilInitialized();

    const { name } = useEventDetails(ref(liveEventStore.event));

    return name.value;
};

const liveEventSubTitle = async () => {
    const liveEventStore = useLiveEventDetailStore();
    await liveEventStore.waitUntilInitialized();

    const { leagueName } = useEventDetails(ref(liveEventStore.event));

    return leagueName.value;
};

const liveEventBackgroundImage = async () => {
    const liveEventStore = useLiveEventDetailStore();
    await liveEventStore.waitUntilInitialized();

    const sportCode = liveEventStore.event?.drilldownNodes.find(
        (node) => node.levelNumber === DRILLDOWN_SPORT_LEVEL_NUMBER,
    )?.code;

    const imageBasePath = `${environment.sportBetsRootUrl}shared-img/covers/`;

    const sportImage = `${imageBasePath}${sportCode}-gradient.jpg`;
    const defaultImage = `${imageBasePath}default.jpg`;

    return `url(${sportImage}), url(${defaultImage})`;
};

const liveEventFavoriteMatchId = async () => {
    const liveEventsStore = useLiveEventDetailStore();
    await liveEventsStore.waitUntilInitialized();

    return liveEventsStore.event?.id;
};

const prematchEventTitle = async (r: RouteLocationNormalized) => {
    const { getRouteIds } = usePrematchRouteIds(r);
    const prematchStore = usePrematchStore();
    await prematchStore.waitUntilInitialized();
    await prematchStore.waitUntilEventInitialized(getRouteIds.value.eventId);
    return prematchStore.events.find((event) => event.id === getRouteIds.value.eventId)?.name;
};

const prematchEventSubTitle = async (r: RouteLocationNormalized) => {
    const { getRouteIds } = usePrematchRouteIds(r);

    const prematchStore = usePrematchStore();
    await prematchStore.waitUntilInitialized();

    const event = prematchStore.events.find((event) => event.id === getRouteIds.value.eventId);
    const league = prematchStore.leagues.find((league) => league.id === getRouteIds.value.leagueId);
    const { eventTime } = useEventScoreAndStatus(ref(event));

    return `${eventTime.value?.startDate} | ${eventTime.value?.startTime} | ${league?.name}`;
};

const prematchEventBackgroundImage = async (r: RouteLocationNormalized) => {
    const { getRouteIds } = usePrematchRouteIds(r);

    const prematchStore = usePrematchStore();
    await prematchStore.waitUntilInitialized();

    const sportCode = prematchStore.sports.find((sport) => sport.id === getRouteIds.value.sportId)?.code;

    const imageBasePath = `${environment.sportBetsRootUrl}shared-img/covers/`;

    const sportImage = `${imageBasePath}${sportCode}-gradient.jpg`;
    const defaultImage = `${imageBasePath}default.jpg`;

    return `url(${sportImage}), url(${defaultImage})`;
};

const prematchEventFavoriteMatchId = async (r: RouteLocationNormalized) => {
    const { getRouteIds } = usePrematchRouteIds(r);

    return getRouteIds.value.eventId;
};

const prematchSportsTitle = async (r: RouteLocationNormalized) => {
    const { getRouteIds } = usePrematchRouteIds(r);
    let title = "KURZOVÁ NABÍDKA";
    if (getRouteIds.value.sportId == PrematchStaticCategories.FAVORITES) {
        title = "Moje oblíbené";
    } else if (getRouteIds.value.sportId == PrematchStaticCategories.MOST_POPULAR) {
        // Should be the same as initial value hence do nothing here
    } else {
        const prematchStore = usePrematchStore();
        await prematchStore.waitUntilInitialized();
        if (getRouteIds.value.countryId) {
            title = prematchStore.countries.find((country) => country.id === getRouteIds.value.countryId)?.name || "";
        } else if (getRouteIds.value.sportId) {
            const drilldownNodeStore = useDrilldownNodesStore();
            const priceBoostSport = await drilldownNodeStore.getSportByCode(Sport.PriceBoost);
            title =
                getRouteIds.value.sportId == priceBoostSport?.id
                    ? "Navýšené kurzy"
                    : prematchStore.sports.find((sport) => sport.id === getRouteIds.value.sportId)?.name || "";
        }
    }
    return title;
};
const prematchSportsBackPath = (r: RouteLocationNormalized) => {
    const { getRouteIds } = usePrematchRouteIds(r);
    let backPath = PATHS.PREMATCH;
    if (getRouteIds.value.sportId == PrematchStaticCategories.MOST_POPULAR) {
        backPath = null;
    } else if (getRouteIds.value.countryId) {
        backPath = PATHS.PREMATCH + `/${getRouteIds.value.sportId}`;
    }
    return backPath;
};

const prematchLeaguesTitle = async (r: RouteLocationNormalized) => {
    const { getRouteIds } = usePrematchRouteIds(r);
    let title = "";
    if (getRouteIds.value.leagueId) {
        const prematchStore = usePrematchStore();
        await prematchStore.waitUntilInitialized();
        title = prematchStore.leagues.find((league) => league.id === getRouteIds.value.leagueId)?.name || "";
    }
    return title;
};

const commonBackPath = (r: RouteLocationNormalized) => {
    return r.fullPath.split("/").slice(0, -1).join("/");
};

const determineBackPath = (router: Router, to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    const { getRouteIds } = usePrematchRouteIds();

    // Check if we're on a root page OR Trending (which acts like a root page for some reason)
    const rootPages = ["Homepage", "Live", "Prematch"];
    const isTrending = getRouteIds.value.sportId == PrematchStaticCategories.MOST_POPULAR;

    // Only hide back button on root pages when there are no query params
    if ((rootPages.includes(to.name as string) && Object.keys(to.query).length === 0) || isTrending) {
        return null;
    }

    // For root pages with query params, we want to provide a back navigation option (I guess)
    if ((rootPages.includes(to.name as string) && Object.keys(to.query).length > 0) || isTrending) {
        const pathWithoutQuery = to.path;
        return pathWithoutQuery;
    }

    // Check if we have browser history to work with
    if (window.history.length > 2) {
        try {
            const previousState = window.history.state;

            if (previousState) {
                if (router.currentRoute.value.query?.timeFilter) {
                    router.replace({
                        path: router.currentRoute.value.path,
                        query: _omit(router.currentRoute.value.query, "timeFilter"),
                        hash: router.currentRoute.value.hash,
                    });
                }

                if (previousState.position > 0 && !previousState.back.includes("?eventId")) {
                    return "$ROUTER_BACK$";
                }
            }
        } catch (e) {
            console.warn("Error checking history state:", e);
        }
    }

    // If history navigation isn't appropriate, use our routing rules
    const routeHeader = routeHeaders.find((rh) => rh.name === to.name);
    if (!routeHeader) return PATHS.HOME;

    if (routeHeader?.backPathPrevious) {
        // Don't go back to the same base path with different query params!
        return from.path !== to.path ? from.fullPath : PATHS.HOME;
    } else if (routeHeader?.backPath) {
        return routeHeader.backPath(to);
    }

    // General fallback
    return PATHS.HOME;
};

const routeHeaders: RouteHeader[] = [
    { name: "Homepage", title: () => "", displaySazkaBetLogo: true },
    { name: "Live", title: () => "LIVE NABÍDKA" },
    {
        name: "LiveEventDetail",
        title: async () => await liveEventTitle(),
        subTitle: async () => await liveEventSubTitle(),
        transparent: true,
        showMiddleText: true,
        backPath: () => PATHS.LIVE,
        backgroundImage: async () => await liveEventBackgroundImage(),
        favoriteMatchId: async () => await liveEventFavoriteMatchId(),
    },
    { name: "Prematch", title: () => "KURZOVÁ NABÍDKA" },
    {
        name: "PrematchSport",
        title: async (r: RouteLocationNormalized) => await prematchSportsTitle(r),
        backPath: (r: RouteLocationNormalized) => prematchSportsBackPath(r),
    },
    {
        name: "PrematchLeagueDetail",
        title: async (r: RouteLocationNormalized) => await prematchLeaguesTitle(r),
        backPath: (r: RouteLocationNormalized) => commonBackPath(r),
    },
    {
        name: "PrematchEventDetail",
        title: async (r: RouteLocationNormalized) => await prematchEventTitle(r),
        subTitle: async (r: RouteLocationNormalized) => await prematchEventSubTitle(r),
        transparent: true,
        showMiddleText: true,
        backPath: (r: RouteLocationNormalized) => commonBackPath(r),
        backgroundImage: async (r: RouteLocationNormalized) => await prematchEventBackgroundImage(r),
        favoriteMatchId: async (r: RouteLocationNormalized) => await prematchEventFavoriteMatchId(r),
    },
    { name: "Ticket", title: () => "Tiket", backPathPrevious: true },
    {
        name: "TicketSettings",
        title: () => "Nastavení tiketu",
        backPath: () => PATHS.TICKET,
        savePreviousBackPath: true,
        closeIcon: true,
    },
    { name: "MyTickets", title: () => "Moje tikety", backPathPrevious: true },
    { name: "Search", title: () => "Vyhledávání", backPathPrevious: true },
];

export const useHeader = defineStore("Header", () => {
    const router = useRouter();
    // state
    const text: Ref<string> = ref("");
    const textPrevious: Ref<string> = ref("");

    const subText: Ref<string> = ref("");
    const subTextPrevious: Ref<string> = ref("");

    const transparent: Ref<boolean> = ref(false);
    const transparentPrevious: Ref<boolean> = ref(false);

    const backLinkEnabled: Ref<boolean> = ref(false);
    const backLink: Ref<string> = ref(null);
    const backLinkPrevious: Ref<string> = ref(null);

    const onClickAction: Ref<() => void | undefined> = ref();

    const showMiddleText = ref<boolean>(false);
    const showMiddleTextPrevious = ref<boolean>(false);

    const backgroundImage: Ref<string> = ref("");
    const backgroundImagePrevious: Ref<string> = ref("");

    const displaySazkaBetLogo = ref<boolean>(false);
    const displaySazkaBetLogoPrevious = ref<boolean>(false);

    const favoriteMatchId: Ref<string> = ref(null);
    const favoriteMatchIdPrevious: Ref<string> = ref(null);

    const closeIcon: Ref<boolean> = ref(false);
    const closeIconPrevious: Ref<boolean> = ref(false);

    const sidebarOpen = ref(false);
    const displayBalance = ref(true);

    const uiStateStore = useUiStateStore();

    // actions
    const setHeader = (
        newText: string,
        newSubText: string,
        newTransparent: boolean,
        newShowMiddleText: boolean,
        newBackgroundImage: string,
        newBackLink?: string | null,
        newDisplaySazkaBetLogo?: boolean,
        newFavoriteMatchId?: string,
        newCloseIcon?: boolean,
        savePreviousBackLink?: boolean,
    ) => {
        setHeaderText(newText);
        setHeaderSubText(newSubText);
        setHeaderTransparent(newTransparent);
        setShowMiddleText(newShowMiddleText);
        setBackgroundImage(newBackgroundImage);
        setDisplaySazkaBetLogo(newDisplaySazkaBetLogo);
        setFavoriteMatchId(newFavoriteMatchId);
        setCloseIcon(newCloseIcon);
        if (newBackLink !== undefined) setHeaderBackLink(newBackLink, !!savePreviousBackLink);
    };

    const setHeaderText = (newText: string, savePreviousValue: boolean = false) => {
        if (text.value === newText) return;
        if (savePreviousValue) textPrevious.value = text.value;
        text.value = newText;
    };
    const setHeaderSubText = (newSubText: string, savePreviousValue: boolean = false) => {
        if (subText.value === newSubText) return;
        if (savePreviousValue) subTextPrevious.value = subText.value;
        subText.value = newSubText;
    };
    const setHeaderTransparent = (newTransparent: boolean, savePreviousValue: boolean = false) => {
        if (transparent.value === newTransparent) return;
        if (savePreviousValue) transparentPrevious.value = transparent.value;
        transparent.value = newTransparent;
    };

    const setShowMiddleText = (newShowMiddleText: boolean, savePreviousValue: boolean = false) => {
        if (showMiddleText.value === newShowMiddleText) return;
        if (savePreviousValue) showMiddleTextPrevious.value = showMiddleText.value;
        showMiddleText.value = newShowMiddleText;
    };

    const setHeaderBackLink = (newBackLink: string | null, savePreviousValue: boolean = false) => {
        if (backLink.value === newBackLink) return;
        if (savePreviousValue) backLinkPrevious.value = backLink.value;
        backLink.value = newBackLink;
    };

    const setBackgroundImage = (newBackgroundImage: string, savePreviousValue: boolean = false) => {
        if (backgroundImage.value === newBackgroundImage) return;
        if (savePreviousValue) backgroundImagePrevious.value = backgroundImage.value;
        backgroundImage.value = newBackgroundImage;
    };

    const setDisplaySazkaBetLogo = (newDisplaySazkaBetLogo: boolean, savePreviousValue: boolean = false) => {
        if (displaySazkaBetLogo.value === newDisplaySazkaBetLogo) return;
        if (savePreviousValue) displaySazkaBetLogoPrevious.value = displaySazkaBetLogo.value;
        displaySazkaBetLogo.value = newDisplaySazkaBetLogo;
    };

    const setFavoriteMatchId = (newFavoriteMatchId: string, savePreviousValue: boolean = false) => {
        if (favoriteMatchId.value === newFavoriteMatchId) return;
        if (savePreviousValue) favoriteMatchIdPrevious.value = favoriteMatchId.value;
        favoriteMatchId.value = newFavoriteMatchId;
    };

    const setCloseIcon = (newCloseIcon: boolean, savePreviousValue: boolean = false) => {
        if (closeIcon.value === newCloseIcon) return;
        if (savePreviousValue) closeIconPrevious.value = closeIcon.value;
        closeIcon.value = newCloseIcon;
    };

    async function scrollToElement(target: string, id: string) {
        await waitUntilTrue(() => uiStateStore.uiState === "idle", 100, 3000); // Wait until the loading is done
        await nextTick(); // Wait for vue to render elements

        const element = document.querySelector(`[data-${target}="${id}"]`);
        if (element) {
            const rect = element.getBoundingClientRect();
            const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;
            if (!isInViewport) {
                // Cant scrollIntoView with block start because the header covers the target element
                // For some reason it does not take in account the header height when scrolling
                element.scrollIntoView({ block: "center" });
            }
        }
    }

    async function checkRemovedParamsAndScrollToElement(
        to: RouteLocationNormalized,
        from: RouteLocationNormalizedLoaded,
    ) {
        const removedParams = Object.keys(from.params).filter((param) => !(param in to.params));
        if (removedParams.includes("eventId")) {
            const eventId = Array.isArray(from.params.eventId) ? from.params.eventId[0] : from.params.eventId;
            await scrollToElement("event-id", eventId);
        } else if (removedParams.includes("leagueId")) {
            const leagueId = Array.isArray(from.params.leagueId) ? from.params.leagueId[0] : from.params.leagueId;
            await scrollToElement("league-id", leagueId);
        } else if (removedParams.includes("sportAndCountryId")) {
            const sportAndCountryId = Array.isArray(from.params.sportAndCountryId)
                ? from.params.sportAndCountryId[0]
                : from.params.sportAndCountryId;
            // It is the same component which renders leagues so we use same league-id target
            await scrollToElement("league-id", sportAndCountryId);
        } else if (from.name === "LiveEventDetail" && to.name === "Live" && removedParams.includes("id")) {
            const id = Array.isArray(from.params.id) ? from.params.id[0] : from.params.id;
            // It is the same component which renders events so we use same event-id target
            await scrollToElement("event-id", id);
        }
    }

    router.afterEach(async (to, from) => {
        if (to.fullPath == from.fullPath && to.path != PATHS.HOME) return;
        backLinkEnabled.value = true;

        const routeHeader = routeHeaders.find((rh) => rh.name === to.name);

        if (!routeHeader) return;

        const newBackLink = determineBackPath(router, to, from);

        const newText = await routeHeader?.title(to);
        const newSubText = routeHeader?.subTitle ? await routeHeader?.subTitle(to) : null;
        const newTransparent = routeHeader?.transparent || false;
        const newShowMiddleText = routeHeader?.showMiddleText || false;
        const newBackgroundImage = routeHeader?.backgroundImage ? await routeHeader?.backgroundImage(to) : null;
        const newDisplaySazkaBetLogo = routeHeader?.displaySazkaBetLogo || false;
        const newFavoriteMatchId = routeHeader?.favoriteMatchId ? await routeHeader?.favoriteMatchId(to) : null;
        const newCloseIcon = routeHeader?.closeIcon || false;
        const newSavePreviousBackLink = routeHeader?.savePreviousBackPath || false;

        //Do the init before awaiting stuff - if eventID cant be find, the back link icon won't show up until 10 seconds of trying to load it. User usually want to go back immediately
        setHeaderBackLink(newBackLink, newSavePreviousBackLink);
        setHeaderTransparent(newTransparent);
        setShowMiddleText(newShowMiddleText);
        setDisplaySazkaBetLogo(newDisplaySazkaBetLogo);
        setCloseIcon(newCloseIcon);

        //specific case for MyTicketDetail - we do not want to have separate view for mobile so we use query instead and handle the header there
        if (to.name == "MyTickets" && to.query.tiket) {
            const { formatDate } = useUtils();

            let ticket: BetData = null;
            try {
                ticket = await activeTicket(to.query.tiket);
            } catch (error) {
                logException("header", error, {
                    message: "An error has occurred while fetching active ticket for mobile header",
                    ticketId: to.query.tiket,
                });
            }
            if (!ticket) return;
            const ticketDate = Object?.values(formatDate(ticket?.placedDate)).join("  |  ");

            return setHeader(
                ticket.name.toUpperCase() + " tiket",
                ticketDate,
                false,
                false,
                "",
                newBackLink,
                false,
                ticket.id,
            );
        }

        if (to.name == "Homepage" && to.query["sdileny-tiket"]) {
            const { formatDate } = useUtils();
            let ticket = null;
            try {
                ticket = await activeSharedTicket(to.query["sdileny-tiket"]);
            } catch (error) {
                logException("header", error, {
                    message: "An error has occurred while fetching active shared ticket for mobile header",
                    ticketId: to.query["sdileny-tiket"],
                });
            }

            if (!ticket) return;

            const ticketDate = Object?.values(formatDate(ticket?.placedDate)).join("  |  ");
            return setHeader(
                ticket.name.toUpperCase() + " tiket" || "Sdílený tiket",
                ticketDate,
                false,
                false,
                "",
                newBackLink,
                false,
                ticket.ticket_id_original,
            );
        }

        setHeader(
            newText,
            newSubText,
            newTransparent,
            newShowMiddleText,
            newBackgroundImage,
            newBackLink,
            newDisplaySazkaBetLogo,
            newFavoriteMatchId,
            newCloseIcon,
            newSavePreviousBackLink,
        );
    });

    return {
        // state
        text,
        textPrevious,
        subText,
        subTextPrevious,
        transparent,
        transparentPrevious,
        backLinkEnabled,
        backLink,
        backLinkPrevious,
        onClickAction,
        showMiddleText,
        showMiddleTextPrevious,
        backgroundImage,
        backgroundImagePrevious,
        displaySazkaBetLogo,
        displaySazkaBetLogoPrevious,
        favoriteMatchId,
        favoriteMatchIdPrevious,
        closeIcon,
        closeIconPrevious,
        sidebarOpen,
        displayBalance,

        // actions
        setHeader,
        setHeaderText,
        setHeaderSubText,
        setHeaderTransparent,
        setShowMiddleText,
        setHeaderBackLink,
        setBackgroundImage,
        setDisplaySazkaBetLogo,
        checkRemovedParamsAndScrollToElement,
    };
});

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useHeader, import.meta.hot));
}
